import './OverviewPageStats.css';
import { useState, useContext, useEffect } from 'react';
import OverviewStatsContext from '../../contexts/data/OverviewStatsContext';
import ReactApexChart from 'react-apexcharts';
import {
  BankOutlined,
  BarChartOutlined,
  BellOutlined,
  BulbOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ContactsOutlined,
  EyeInvisibleFilled,
  EyeOutlined,
  GlobalOutlined,
  LeftOutlined,
  LineChartOutlined,
  MessageOutlined,
  PhoneOutlined,
  ReloadOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { USERS_PAGE_PATH } from '../../pages/users/UsersPage';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';
import { NUMBERS_PAGE_PATH } from '../../pages/numbers/NumbersPage';
import StatsTile from '../@shared/StatsTile';
import { Button, Card, Divider, Row, Skeleton, Tooltip } from 'antd';
import { formatNumber, } from '@leedpulse-monorepo/utils';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import SubscriptionDataSource, {
  subscriptionDataSourceAsString,
} from '../../../domain/enums/subscription/subscription_datasource/SubscriptionDataSource';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useNavigate } from 'react-router-dom';
import { ApexOptions } from 'apexcharts';

dayjs.extend(localizedFormat);

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface OverviewPageStatsProps {}

const OverviewPageStats: React.FC<OverviewPageStatsProps> = (_) => {
  const overviewStatsContext = useContext(OverviewStatsContext);
  const navigate = useNavigate();

  const categories = overviewStatsContext.chartsData.categories || [];

  interface StatsItem {
    icon: React.ReactNode;
    title: string;
    value: number | string;
    pagePath: string;
    isCondifential: boolean;
  }

  const [statsItems, setStatsItems] = useState<StatsItem[]>([]);
  const [statsCurrentItems, setStatsCurrentItems] = useState<StatsItem[]>([]);
  const [isConfidential, setIsConfidential] = useState(true);
  const [currentDate, setCurrentDate] = useState(dayjs());

  useEffect(() => {
    overviewStatsContext.fetch();
    overviewStatsContext.fetchCharts();
    overviewStatsContext.fetchCRMStats();
    overviewStatsContext.fetchTodaySMSStats(currentDate);
    overviewStatsContext.fetchMessagesStats(currentDate);
  }, []);

  useEffect(() => {
    overviewStatsContext.fetchMessagesStats(currentDate);
    overviewStatsContext.fetchTodaySMSStats(currentDate);
  }, [currentDate]);

  useEffect(() => {
    try {
      // statsItems a trois ecrivains ; celui-ci reconstruit la liste complete a
      // partir du contexte. Le deriver suppose d'unifier les trois d'abord.
      // eslint-disable-next-line react/set-state-in-effect
      setStatsItems([
        {
          icon: <UserOutlined />,
          title: 'Utilisateurs',
          value: formatNumber(overviewStatsContext.usersCount),
          pagePath: USERS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <CalendarOutlined />,
          title: 'Abonnements',
          value: formatNumber(overviewStatsContext.subscriptionsCount),
          pagePath: SUBSCRIPTIONS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <PhoneOutlined />,
          title: 'Numéros',
          value: formatNumber(overviewStatsContext.numbersCount),
          pagePath: NUMBERS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <ContactsOutlined />,
          title: 'Contacts',
          value: formatNumber(overviewStatsContext.contactsCount),
          pagePath: CONTACTS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <MessageOutlined />,
          title: 'Reçus & envoyés',
          value: formatNumber(overviewStatsContext.smsCount),
          pagePath: CONTACTS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <ClockCircleOutlined />,
          title: 'Messages planifiés',
          value: formatNumber(overviewStatsContext.jobsCount),
          pagePath: CONTACTS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <BankOutlined />,
          title: 'Coût total SMS',
          value:
            formatNumber(Math.floor(overviewStatsContext.totalPriceSms)) + '€',
          pagePath: CONTACTS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <LineChartOutlined />,
          title: 'Taux de réponse',
          value: formatNumber(overviewStatsContext.replyRate) + '%',
          pagePath: CONTACTS_PAGE_PATH,
          isCondifential: isConfidential,
        },
      ]);
    } catch (error) {
      setStatsItems([]);
    }
  }, [overviewStatsContext.status]);

  useEffect(() => {
    try {
      // L'effet est couple a une ref (garde de pagination, generation de fetch) :
      // ecrire cette ref pendant le rendu serait une autre violation. On garde l'effet.
      // eslint-disable-next-line react/set-state-in-effect
      setStatsCurrentItems([
        {
          icon: <MessageOutlined />,
          title: 'Reçus & envoyés',
          value: formatNumber(overviewStatsContext.currentSmsCount),
          pagePath: CONTACTS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <ClockCircleOutlined />,
          title: 'Messages planifiés',
          value: formatNumber(overviewStatsContext.currentJobsCount),
          pagePath: CONTACTS_PAGE_PATH,
          isCondifential: isConfidential,
        },
        {
          icon: <BellOutlined />,
          title: 'Notifications',
          value: formatNumber(overviewStatsContext.currentNotificationsCount),
          pagePath: CONTACTS_PAGE_PATH,
          isCondifential: isConfidential,
        },
      ]);
    } catch (e) {
      setStatsCurrentItems([]);
    }
  }, [overviewStatsContext.messagesStatus]);

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: {
        show: true,
      },
    },
    xaxis: {
      categories,
      title: {
        text: '60 derniers jours',
      },
    },
    yaxis: {
      title: {
        text: 'Nb de messages',
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 5,
        borderRadiusWhenStacked: 'last',
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '10px',
        colors: ['#fff'],
      },
      offsetY: 2,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => `${val}`,
      },
    },
    colors: [
      '#2d3436e0',
      '#6cbde0',
      '#6ce077',
      '#169c33',
      '#ffed66',
      '#ffad66',
      '#d94e4e',
    ],
    legend: {
      position: 'top',
    },
    fill: {
      opacity: 1,
    },
  };

  // Chart config
  const stackedBarOptions = {
    chart: {
      type: 'bar' as const,
      height: 80,
      stacked: true,
      stackType: '100%',
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '50%',
      },
    },
    xaxis: {
      max: 100,
      labels: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      position: 'bottom',
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`,
    },
    colors: ['#3dbea8', '#531dab', '#f0e289', '#FFA500'],
  };

  const totalContacts = overviewStatsContext.engagedNotAvailableCount || 1;

  const stackedBarSeries = [
    {
      name: 'Intéressés',
      data: [
        (overviewStatsContext.qualificationDistribution.interested /
          totalContacts) *
          100,
      ],
    },
    {
      name: 'En réflexion',
      data: [
        (overviewStatsContext.qualificationDistribution.considering /
          totalContacts) *
          100,
      ],
    },
    {
      name: 'Sans réponse',
      data: [
        (overviewStatsContext.qualificationDistribution.not_responsive /
          totalContacts) *
          100,
      ],
    },
    {
      name: 'Inactifs', // Pas intéressés
      data: [
        (overviewStatsContext.qualificationDistribution.not_interested /
          totalContacts) *
          100,
      ],
    },
  ];

  const todaySmsBarOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    xaxis: {
      categories: Object.keys(overviewStatsContext.todaySMSStats),
    },
    yaxis: {
      title: {
        text: 'Nb de SMS',
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
        horizontal: false,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        colors: ['#2d3436e0'],
      },
      offsetY: -20, 
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => `${val}`,
      },
    },
    colors: ['#169c33', '#6ce077', '#ffad66'],
    legend: {
      position: 'top',
    },
    fill: {
      opacity: 1,
    },
  };

  const todaySmsBarSeries = [
    {
      name: 'Envoyés',
      data: Object.values(overviewStatsContext.todaySMSStats).map(
        (stat) => stat.sent
      ),
    },
    {
      name: 'Reçus',
      data: Object.values(overviewStatsContext.todaySMSStats).map(
        (stat) => stat.received
      ),
    },
    {
      name: 'Planifiés',
      data: Object.values(overviewStatsContext.todaySMSStats).map(
        (stat) => stat.planned
      ),
    },
  ];

  // CRM Chart config

  const crmBarOptions = {
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
      },
      events: {
        click: function(event: any, chartContext: any, opts: any) {
          const clickedCategory = Object.keys(overviewStatsContext.crmStats)[opts.dataPointIndex];

          if (clickedCategory) {
            navigate(`${SUBSCRIPTIONS_PAGE_PATH}?crmUsed=${clickedCategory}`);
          }
        }
      }
    },
    xaxis: {
      categories: Object.keys(overviewStatsContext.crmStats).map((crm) =>
        subscriptionDataSourceAsString(crm as SubscriptionDataSource)
      ),
    },
    yaxis: {
      title: {
        text: 'Nb de clients',
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
        horizontal: false,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        colors: ['#2d3436e0'],
      },
      offsetY: -20, // place le label EN DEHORS de la barre
    },
    tooltip: {
      intersect: false,
      y: {
        formatter: (val: number) => `${val}`,
      },
    },
    colors: ['#6737ff'],
    legend: {
      show: false,
    },
    fill: {
      opacity: 1,
    },
  };

  const crmBarSeries = [
    {
      name: "Nombre d'abonnement(s)",
      data: Object.values(overviewStatsContext.crmStats),
    },
  ];

  const isCurrentDateToday = () => {
    return (
      currentDate.get('D') === dayjs().get('D') &&
      currentDate.get('M') === dayjs().get('M') &&
      currentDate.get('y') === dayjs().get('y')
    );
  };

  return (
    <>
      {statsItems.length === 0 ? (
        <Skeleton active />
      ) : (
        <Row
          align="middle"
          style={{
            marginBottom: '12px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {overviewStatsContext.status === ContextStatus.LOADING ? (
            <Button type="text" loading>
              {isConfidential
                ? 'Afficher les statistiques confidentielles'
                : 'Masquer les statistiques confidentielles'}
            </Button>
          ) : (
            <Button
              icon={isConfidential ? <EyeOutlined /> : <EyeInvisibleFilled />}
              onClick={(e) => {
                // Rewrite all the items in statsItems to set isConfidential to false
                setStatsItems(
                  statsItems.map((item) => ({
                    ...item,
                    isCondifential: !item.isCondifential,
                  }))
                );
                setStatsCurrentItems(
                  statsCurrentItems.map((item) => ({
                    ...item,
                    isCondifential: !item.isCondifential,
                  }))
                );
                setIsConfidential(!isConfidential);
              }}
              type="text"
            >
              {isConfidential
                ? 'Afficher les statistiques confidentielles'
                : 'Masquer les statistiques confidentielles'}
            </Button>
          )}
          <Button
            type="primary"
            onClick={() => {
              overviewStatsContext.fetch();
              overviewStatsContext.fetchCharts();
            }}
            icon={<ReloadOutlined />}
          >
            Rafraîchir
          </Button>
        </Row>
      )}

      <div
        style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}
      >
        <h2 style={{ margin: 0 }}>
          <BarChartOutlined /> {isCurrentDateToday() && "Aujourd'hui, "}le{' '}
          {currentDate.locale('fr').format('LL')}
        </h2>
        <Tooltip title="Jour précédent">
          <Button
            style={{ marginLeft: '1rem' }}
            icon={<LeftOutlined />}
            loading={
              overviewStatsContext.messagesStatus === ContextStatus.LOADING
            }
            onClick={() => {
              const newDate = currentDate.subtract(1, 'day');
              setCurrentDate(newDate);
            }}
          />
        </Tooltip>
        <Tooltip title="Jour suivant">
          <Button
            style={{ marginLeft: '1rem' }}
            icon={<RightOutlined />}
            loading={
              overviewStatsContext.messagesStatus === ContextStatus.LOADING
            }
            onClick={() => {
              const newDate = currentDate.add(1, 'day');
              setCurrentDate(newDate);
            }}
          />
        </Tooltip>
        {!isCurrentDateToday() && (
          <Button
            style={{ marginLeft: '1rem' }}
            loading={
              overviewStatsContext.messagesStatus === ContextStatus.LOADING
            }
            onClick={() => {
              setCurrentDate(dayjs());
            }}
          >
            Aujourd'hui
          </Button>
        )}
      </div>
      <div className="overview-page-stats">
        {statsCurrentItems.map((item, index) => (
          <StatsTile
            key={index}
            isLoading={
              overviewStatsContext.messagesStatus === ContextStatus.LOADING
            }
            item={item}
            isConfidential={item.isCondifential}
          />
        ))}
      </div>

      <Card className="overview-page-stats__crms">
        {overviewStatsContext.todaySMSChartStatus ===
        ContextStatus.LOADING ? (
          <Skeleton active />
        ) : (
          <div>
            <h3><MessageOutlined /> SMS</h3>
            <span className="overview-page-stats__count">
              <ReactApexChart
                options={todaySmsBarOptions as ApexOptions}
                series={todaySmsBarSeries}
                // series={overviewStatsContext.todayMessagesStats}
                type="bar"
                height={300}
              />
            </span>
          </div>
        )}
      </Card>

      <Divider />

      <h2><GlobalOutlined /> Global</h2>
      <div className="overview-page-stats">
        {statsItems.map((item, index) => (
          <StatsTile
            key={index}
            isLoading={overviewStatsContext.status === ContextStatus.LOADING}
            item={item}
            isConfidential={item.isCondifential}
          />
        ))}
      </div>

      <Card className="overview-page-stats__interest">
        {overviewStatsContext.chartStatus === ContextStatus.LOADING ? (
          <Skeleton active />
        ) : (
          <div>
            <h3>
              <BulbOutlined /> Intérêt des prospects{' '}
              <span>
                (statistique calculée sur{' '}
                {formatNumber(overviewStatsContext.engagedNotAvailableCount)}{' '}
                contacts)
              </span>
            </h3>
            <span className="overview-page-stats__count">
              <ReactApexChart
                options={stackedBarOptions as ApexOptions}
                series={stackedBarSeries}
                type="bar"
                height={100}
              />
            </span>
          </div>
        )}
      </Card>

      <Card className="overview-page-stats__crms">
        {overviewStatsContext.crmChartStatus === ContextStatus.LOADING ? (
          <Skeleton active />
        ) : (
          <div>
            <h3><BarChartOutlined /> CRMs utilisés</h3>
            <span className="overview-page-stats__count">
              <ReactApexChart
                options={crmBarOptions as ApexOptions}
                series={crmBarSeries}
                type="bar"
                height={300}
              />
            </span>
          </div>
        )}
      </Card>

      <div className="overview-page-stats__crms">
        <h2><BarChartOutlined /> Activité</h2>
        {overviewStatsContext.chartStatus === ContextStatus.LOADING ? (
          <Card>
            <Skeleton active />
          </Card>
        ) : (
          <Card>
            <ReactApexChart
              options={chartOptions as ApexOptions}
              series={overviewStatsContext.chartsData.series}
              type="bar"
              height={350}
            />
          </Card>
        )}
      </div>
    </>
  );
};

export default OverviewPageStats;
